import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Save,
  ExternalLink,
  Loader2,
  Palette,
  Type,
  Boxes,
  Phone,
  Rocket,
  Image as ImageIcon,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Logo } from "@/components/site/Logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

const TABS = [
  { id: "branding", label: "Branding", icon: Palette },
  { id: "hero", label: "Hero", icon: Type },
  { id: "products", label: "Products", icon: Boxes },
  { id: "contact", label: "Contact", icon: Phone },
  { id: "demo", label: "Demo", icon: Rocket },
];

function Field({ label, children, hint }) {
  return (
    <div className="space-y-2">
      <Label className="font-mono text-[11px] tracking-[0.12em] text-white/60">
        {label.toUpperCase()}
      </Label>
      {children}
      {hint && <p className="font-body text-xs text-white/35">{hint}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { config, reload } = useConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("branding");

  useEffect(() => {
    if (config) setForm(JSON.parse(JSON.stringify(config)));
  }, [config]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const setProduct = (idx, key, value) =>
    setForm((f) => {
      const products = [...f.products];
      products[idx] = { ...products[idx], [key]: value };
      return { ...f, products };
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config", form);
      await reload();
      toast.success("Changes published");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (!form) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#040405]">
        <Loader2 className="h-6 w-6 animate-spin text-white/60" />
      </div>
    );
  }

  const inputCls =
    "border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus-visible:ring-[var(--accent)]";

  return (
    <div className="min-h-screen bg-[#040405] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#040405]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Logo height={26} />
            <span className="font-mono hidden text-[10px] tracking-[0.2em] text-white/40 sm:inline">
              / CMS
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-body hidden text-sm text-white/40 sm:inline">
              {user?.email}
            </span>
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="font-body inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-white/70 transition-colors hover:border-white/30 hover:text-white"
              data-testid="admin-view-site"
            >
              <ExternalLink className="h-4 w-4" /> View site
            </a>
            <Button
              onClick={doLogout}
              variant="ghost"
              size="sm"
              data-testid="admin-logout-btn"
              className="text-white/70 hover:bg-white/5 hover:text-white"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-light tracking-tight">
              Site Content
            </h1>
            <p className="font-body mt-1 text-sm text-white/45">
              Edit and publish changes to the live website.
            </p>
          </div>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="admin-save-btn"
            className="text-white"
            style={{ background: "var(--accent)" }}
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Publish
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-8 flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                data-testid={`admin-tab-${t.id}`}
                className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-2 font-body text-sm text-white/60 data-[state=active]:border-transparent data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white"
              >
                <t.icon className="mr-2 h-4 w-4" />
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Branding */}
          <TabsContent value="branding" className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Company Name">
                <Input
                  className={inputCls}
                  value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  data-testid="field-company-name"
                />
              </Field>
              <Field label="Tagline">
                <Input
                  className={inputCls}
                  value={form.tagline}
                  onChange={(e) => set("tagline", e.target.value)}
                />
              </Field>
              <Field label="Accent Color" hint="Hex value, e.g. #007AFF">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.accent_color}
                    onChange={(e) => set("accent_color", e.target.value)}
                    className="h-10 w-14 cursor-pointer rounded border border-white/10 bg-transparent"
                    data-testid="field-accent-color"
                  />
                  <Input
                    className={inputCls}
                    value={form.accent_color}
                    onChange={(e) => set("accent_color", e.target.value)}
                  />
                </div>
              </Field>
              <div />
              <Field label="Logo URL" hint="Paste an uploaded logo image URL (optional).">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-white/40" />
                  <Input
                    className={inputCls}
                    value={form.logo_url}
                    onChange={(e) => set("logo_url", e.target.value)}
                    placeholder="https://..."
                    data-testid="field-logo-url"
                  />
                </div>
              </Field>
              <Field label="Favicon URL">
                <Input
                  className={inputCls}
                  value={form.favicon_url}
                  onChange={(e) => set("favicon_url", e.target.value)}
                  placeholder="https://..."
                  data-testid="field-favicon-url"
                />
              </Field>
            </div>
          </TabsContent>

          {/* Hero */}
          <TabsContent value="hero" className="space-y-6">
            <Field label="Hero Headline">
              <Input
                className={inputCls}
                value={form.hero_headline}
                onChange={(e) => set("hero_headline", e.target.value)}
                data-testid="field-hero-headline"
              />
            </Field>
            <Field label="Hero Subheadline">
              <Textarea
                className={inputCls}
                rows={2}
                value={form.hero_subheadline}
                onChange={(e) => set("hero_subheadline", e.target.value)}
                data-testid="field-hero-subheadline"
              />
            </Field>
            <Field label="Supporting Paragraph">
              <Textarea
                className={inputCls}
                rows={3}
                value={form.hero_paragraph}
                onChange={(e) => set("hero_paragraph", e.target.value)}
                data-testid="field-hero-paragraph"
              />
            </Field>
          </TabsContent>

          {/* Products */}
          <TabsContent value="products" className="space-y-4">
            {form.products.map((p, i) => (
              <div
                key={p.id}
                className="glass rounded-xl p-5"
                data-testid={`admin-product-${p.id}`}
              >
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Name">
                    <Input
                      className={inputCls}
                      value={p.name}
                      onChange={(e) => setProduct(i, "name", e.target.value)}
                    />
                  </Field>
                  <Field label="Status">
                    <Input
                      className={inputCls}
                      value={p.status}
                      onChange={(e) => setProduct(i, "status", e.target.value)}
                    />
                  </Field>
                  <Field label="Flagship">
                    <div className="flex h-10 items-center">
                      <Switch
                        checked={p.flagship}
                        onCheckedChange={(v) => setProduct(i, "flagship", v)}
                        data-testid={`product-flagship-${p.id}`}
                      />
                    </div>
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Description">
                    <Textarea
                      className={inputCls}
                      rows={2}
                      value={p.description}
                      onChange={(e) =>
                        setProduct(i, "description", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Contact */}
          <TabsContent value="contact" className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Company Email">
                <Input
                  className={inputCls}
                  value={form.company_email}
                  onChange={(e) => set("company_email", e.target.value)}
                  data-testid="field-email"
                />
              </Field>
              <Field label="Phone">
                <Input
                  className={inputCls}
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </Field>
              <Field label="Address">
                <Input
                  className={inputCls}
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                />
              </Field>
              <Field label="Instagram">
                <Input
                  className={inputCls}
                  value={form.instagram}
                  onChange={(e) => set("instagram", e.target.value)}
                  data-testid="field-instagram"
                />
              </Field>
              <Field label="LinkedIn URL">
                <Input
                  className={inputCls}
                  value={form.linkedin}
                  onChange={(e) => set("linkedin", e.target.value)}
                />
              </Field>
              <Field label="X / Twitter URL">
                <Input
                  className={inputCls}
                  value={form.twitter}
                  onChange={(e) => set("twitter", e.target.value)}
                />
              </Field>
            </div>
          </TabsContent>

          {/* Demo */}
          <TabsContent value="demo" className="space-y-6">
            <Field
              label="Crash Ready Demo URL"
              hint="The primary CTAs redirect here after the transition."
            >
              <Input
                className={inputCls}
                value={form.crash_ready_demo_url}
                onChange={(e) => set("crash_ready_demo_url", e.target.value)}
                placeholder="https://..."
                data-testid="field-demo-url"
              />
            </Field>
            <div className="glass flex items-center justify-between rounded-xl p-5">
              <div>
                <p className="font-display font-medium text-white">
                  Cinematic Demo Transition
                </p>
                <p className="font-body mt-1 text-sm text-white/45">
                  Show the fade-to-black initializing sequence before redirect.
                </p>
              </div>
              <Switch
                checked={form.demo_transition_enabled}
                onCheckedChange={(v) => set("demo_transition_enabled", v)}
                data-testid="field-demo-transition"
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
